import React, { useEffect, useState } from 'react';
import { Container, Divider, Grid, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useLocale } from 'hooks';

import { DashboardTile, DesktopPage, Loader } from 'ui';
import { InstallerIcon, ManufacturerIcon, OwnerIcon, VerifierIcon } from 'ui/icons';
import { Asset, AssetStatuses } from 'hooks/useAssets/useAssets.types';
import { AppRoute } from 'routing/AppRoute.enum';

import { useStyles } from './DesktopDashboard.styles';
import { DesktopDashboardProps } from './DesktopDashboard.types';

const statusesInitial = {
  [AssetStatuses.ACTIVE]: [],
  [AssetStatuses.INACTIVE]: [],
  [AssetStatuses.INSTALLED]: [],
  [AssetStatuses.VERIFIED]: [],
  [AssetStatuses.ISSUED]: [],
};

export const DesktopDashboard = ({ batteries }: DesktopDashboardProps) => {
  const theme = useTheme();
  const { push } = useHistory();
  const { formatMessage } = useLocale();
  const classes = useStyles();

  const [statuses, setStatuses] = useState<Record<AssetStatuses, Asset[]>>(statusesInitial);

  useEffect(() => {
    if (batteries) {
      const readyStatuses = batteries.reduce((acc, asset) => {
        return {
          ...acc,
          [asset.status]: [...acc[asset.status], asset],
        };
      }, statusesInitial);
      setStatuses(readyStatuses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batteries]);

  const renderContent = () => (
    <Container disableGutters>
      <div className={classes.textContainer}>
        <Typography className={classes.mainTitle}>{formatMessage({ id: 'navigation.dashboard' })}</Typography>
        <Typography className={classes.subtitle}>{formatMessage({ id: 'navigation.home' })}</Typography>
        <Typography className={classes.subtitle}>|</Typography>
        <Typography className={classes.subtitle}>{formatMessage({ id: 'navigation.dashboard' })}</Typography>
      </div>
      <Divider className={classes.divider} />
      <Grid container spacing={2} className={classes.container}>
        <Grid item xs={12} md={3}>
          <DashboardTile
            icon={ManufacturerIcon}
            number={statuses.ISSUED.length}
            title={formatMessage({ id: 'desktop.dashboard.inactive' })}
            onClick={() => push(AppRoute.desktopDashboardGrid, { statuses: [AssetStatuses.ISSUED] })}
            color={theme.palette.red.main}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DashboardTile
            icon={InstallerIcon}
            number={statuses.INSTALLED.length}
            title={formatMessage({ id: 'desktop.dashboard.installed' })}
            onClick={() => push(AppRoute.desktopDashboardGrid, { statuses: [AssetStatuses.INSTALLED] })}
            color={theme.palette.yellow.main}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DashboardTile
            icon={VerifierIcon}
            number={statuses.VERIFIED.length}
            title={formatMessage({ id: 'desktop.dashboard.verified' })}
            onClick={() => push(AppRoute.desktopDashboardGrid, { statuses: [AssetStatuses.VERIFIED] })}
            color={theme.palette.secondary.main}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DashboardTile
            icon={OwnerIcon}
            number={statuses.ACTIVE.length}
            title={formatMessage({ id: 'desktop.dashboard.active' })}
            onClick={() => push(AppRoute.desktopDashboardGrid, { statuses: [AssetStatuses.ACTIVE] })}
            color={theme.palette.primary.light}
          />
        </Grid>
      </Grid>
    </Container>
  );

  return <DesktopPage>{batteries ? renderContent() : <Loader suppressText fullScreen />}</DesktopPage>;
};
